import { LitElement, html, css, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { RDPropsetContainer } from '../Container/PropContainer.js';
import { icons } from '../../../Icons.js';

import styles from './Selection.css?raw';

export class propSelection extends RDPropsetContainer {
  static styles = [
    super.styles,
    css`
      ${unsafeCSS(styles)}
    `,
  ];
  static properties = {
    value: { type: Object }, // Can be string, number, object, etc.
    selectedIndex: { type: Number, reflect: true },
    disabled: { type: Boolean, reflect: true },
    items: { type: Array },
    selectedIndexChanged: { type: Function },
    onClick: { type: Function },
    callback: { type: Function },
    fontSize: { type: String },
    toggle: { type: Boolean, reflect: true },
  };
  constructor() {
    super();
    this.value = '';
    this.selectedIndex = -1;
    this.disabled = false;
    this.callback = null;
    this.fontSize = null;
    this.toggle = false;
    this.selectedIndexChanged = null;
    this.onClick = null;
    /**
     * @type {Array<{tooltip: string, label: string, value: any, icon?: string}>}
     */
    this.items = [];
  }
  firstUpdated() {
    super.firstUpdated();
    this._renderIcons();
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    this._renderIcons();
  }
  _onItemSelected(item, index) {
    if (this.disabled) return;

    let shouldInvokeCallbacks = true;

    // If toggle is true and clicking the same item, unselect it
    if (this.toggle === true && this.selectedIndex === index) {
      this.selectedIndex = -1;
      this.value = null;
    }
    // If toggle is false and clicking the same item, just fire onClick
    else if (this.toggle === false && this.selectedIndex === index) {
      shouldInvokeCallbacks = false;
    }
    // Otherwise, select the new item
    else {
      this.selectedIndex = index;
      this.value = this.items[index];
    }

    // Fire callbacks only when value actually changes
    if (shouldInvokeCallbacks) {
      if (this.callback && typeof this.callback === 'function') {
        this.callback.bind(this)(this.value, this.selectedIndex);
      }
      if (this.selectedIndexChanged && typeof this.selectedIndexChanged === 'function') {
        this.selectedIndexChanged.bind(this)(this.value, this.selectedIndex);
      }
    }

    // Always fire onClick
    if (this.onClick && typeof this.onClick === 'function') {
      this.onClick.bind(this)(this.value, this.selectedIndex);
    }

    // Always dispatch event
    this.dispatchEvent(new CustomEvent('prop-changed', { detail: { value: this.value, index: this.selectedIndex } }));
    this.requestUpdate();
  }
  _renderItems() {
    return html`<div class="selection-items">${this.items.map((item, index) => this._renderItem(item, index))}</div>`;
  }
  _renderItem(item, index) {
    return html` <div class="selection-item ${this.selectedIndex === index ? 'selected' : ''} ${this.disabled ? 'disabled' : ''}" @click=${() => this._onItemSelected(item, index)}>
      <div class="item-icon">
        <slot name="icon-${index}"></slot>
      </div>
    </div>`;
  }
  _renderIcons() {
    const allIcons = this.querySelectorAll('.icon-svg');
    allIcons.forEach((icon) => icon.remove());
    return this.items.map((item, index) => {
      const iconSVG = item.icon ? item.icon : item.label ? item.label : '';
      const iconElement = document.createElement('span');
      iconElement.style.fontSize = this.fontSize || 'inherit';
      iconElement.classList.add('icon-svg');
      iconElement.innerHTML = iconSVG;
      iconElement.setAttribute('slot', `icon-${index}`);
      iconElement.setAttribute('title', item.tooltip || '');
      this.appendChild(iconElement);
    });
  }

  renderBody() {
    return html` <div class="selection-container">${this._renderItems()}</div>`;
  }
}
if (!customElements.get('rd-prop-selection')) {
  customElements.define('rd-prop-selection', propSelection);
}
